/**
 * Distro Product Simplify view: Commitment Status + License/Match Status.
 */

const DAY_COUNTDOWN_RE = /^(-?\d+)\s+\((\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\)$/
const PENDING_MONTHS_RE = /^(\d+)\s+months?$/i
const ACTIVE_YEARS_RE =
  /^(-?\d+\.\d+)\s+years?(?:\s+\((\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\))?$/i
const PENDING_YEARS_RE = /^(\d+)\s+years?$/i

function blankish(value) {
  if (value == null) return true
  const s = String(value).trim()
  if (!s || s === '—') return true
  const lower = s.toLowerCase()
  return lower === 'nan' || lower === 'none' || lower === 'null'
}

function str(value) {
  return blankish(value) ? '' : String(value).trim()
}

function isYnYes(value) {
  if (value === true || value === 1) return true
  const s = String(value ?? '')
    .trim()
    .toLowerCase()
  return s === 'y' || s === 'yes' || s === 'true' || s === '1'
}

/** Parse ``"# (MM/DD/YY)"`` or ``"# (MM/DD/YYYY)"`` → { days, endDate }. */
export function parseDistroDayCountdown(value) {
  const s = str(value)
  if (!s) return null
  const m = s.match(DAY_COUNTDOWN_RE)
  if (!m) return null
  const days = Number(m[1])
  let year = Number(m[4])
  if (m[4].length === 2) year = 2000 + year
  const month = Number(m[2])
  const day = Number(m[3])
  const end = new Date(Date.UTC(year, month - 1, day))
  if (
    Number.isNaN(end.getTime()) ||
    end.getUTCFullYear() !== year ||
    end.getUTCMonth() !== month - 1 ||
    end.getUTCDate() !== day
  ) {
    return null
  }
  return { days, end }
}

function parseIsoDate(value) {
  const s = str(value)
  if (!s) return null
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const end = new Date(Date.UTC(year, month - 1, day))
  if (
    Number.isNaN(end.getTime()) ||
    end.getUTCFullYear() !== year ||
    end.getUTCMonth() !== month - 1 ||
    end.getUTCDate() !== day
  ) {
    return null
  }
  return end
}

/** M/D/YY without zero padding, matching the countdown strings it is parsed from. */
function formatEndMdY(end) {
  const m = end.getUTCMonth() + 1
  const d = end.getUTCDate()
  const yy = String(end.getUTCFullYear()).slice(-2)
  return `${m}/${d}/${yy}`
}

function daysUntilUtc(end, today = new Date()) {
  const t = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  const e = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  return Math.round((e - t) / 86400000)
}

function parseIntOrNull(value) {
  if (blankish(value)) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function pendingMonths(value) {
  const m = str(value).match(PENDING_MONTHS_RE)
  return m ? Number(m[1]) : null
}

function pendingYearsInt(value) {
  const m = str(value).match(PENDING_YEARS_RE)
  return m ? Number(m[1]) : null
}

function isPendingYears(value) {
  return PENDING_YEARS_RE.test(str(value))
}

/** Parse ``\"4.8 years\"`` or ``\"4.8 years (04/10/34)\"`` → { years, end }. */
function parseActiveLicenseYears(value) {
  const m = str(value).match(ACTIVE_YEARS_RE)
  if (!m) return null
  let end = null
  if (m[2]) {
    let year = Number(m[4])
    if (m[4].length === 2) year = 2000 + year
    const month = Number(m[2])
    const day = Number(m[3])
    const d = new Date(Date.UTC(year, month - 1, day))
    if (
      !Number.isNaN(d.getTime()) &&
      d.getUTCFullYear() === year &&
      d.getUTCMonth() === month - 1 &&
      d.getUTCDate() === day
    ) {
      end = d
    }
  }
  return { years: m[1], end }
}

function extendSuffix(licenseExtend) {
  return licenseExtend ? ' (with a match to extend)' : ''
}

function withOptionAfterRemaining(phrase, optionPeriod) {
  if (!optionPeriod || !phrase) return phrase
  if (!phrase.includes('remaining')) return phrase
  return phrase.replace(/remaining/, 'remaining + Option')
}

/**
 * Later of Days Remaining end, Tracks Tail end, Period End, and Tracks Fulfilled On.
 * Tracks-only deals (no time commitment) complete on the Nth release date.
 */
function commitmentEndDate(row, daysCd, tailCd) {
  const candidates = []
  if (tailCd?.end) candidates.push(tailCd.end)
  if (daysCd?.end) candidates.push(daysCd.end)
  const periodEnd = parseIsoDate(row?.['Period End'])
  if (periodEnd) candidates.push(periodEnd)
  const fulfilledOn = parseIsoDate(row?.['Tracks Fulfilled On'])
  if (fulfilledOn) candidates.push(fulfilledOn)
  if (!candidates.length) return null
  return candidates.reduce((a, b) => (a.getTime() >= b.getTime() ? a : b))
}

function commitmentCompletedPhrase(end, optionPeriod) {
  if (optionPeriod) {
    if (!end) return 'Commitment completed, but we have an Option'
    return `Commitment completed ${formatEndMdY(end)}, but we have an Option`
  }
  if (!end) return 'Commitment Completed'
  return `Commitment Completed ${formatEndMdY(end)}`
}

function buildCommitmentStatus(row, today) {
  const contractStart = str(row?.['Contract Start'] || row?.['Period Start'])
  if (!contractStart) return 'No contract start'

  const optionPeriod = isYnYes(row?.['Option Period'])
  const tracksRemaining = parseIntOrNull(row?.['Tracks Remaining'])
  const daysCd = parseDistroDayCountdown(row?.['Days Remaining'])
  const tailRaw = str(row?.['Tracks Tail'])
  const tailCd = parseDistroDayCountdown(tailRaw)
  const tailMonths = pendingMonths(tailRaw)

  // Tail already counting down → later of Days Remaining end and Tail end.
  if (tailCd) {
    let end = tailCd.end
    if (daysCd && daysCd.end.getTime() > end.getTime()) {
      end = daysCd.end
    }
    const days = daysUntilUtc(end, today)
    if (days <= 0 && (tracksRemaining == null || tracksRemaining <= 0)) {
      return commitmentCompletedPhrase(
        commitmentEndDate(row, daysCd, tailCd) || end,
        optionPeriod,
      )
    }
    return withOptionAfterRemaining(
      `${days} days (${formatEndMdY(end)}) remaining`,
      optionPeriod,
    )
  }

  const tracksOpen = tracksRemaining != null && tracksRemaining > 0
  const daysOpen = daysCd != null && daysCd.days > 0

  if (tracksOpen || daysOpen) {
    const segs = []
    if (tracksOpen) {
      const unit = Math.abs(tracksRemaining) === 1 ? 'track' : 'tracks'
      let trackSeg = `${tracksRemaining} ${unit}`
      if (tailMonths != null) {
        trackSeg += ` (+${tailMonths} month tail)`
      }
      segs.push(trackSeg)
    }
    if (daysCd && (daysOpen || tracksOpen)) {
      segs.push(`${daysCd.days} days (${formatEndMdY(daysCd.end)}) remaining`)
    }
    let phrase
    if (segs.length === 2) phrase = `${segs[0]} or ${segs[1]}`
    else if (segs.length === 1) {
      phrase = segs[0].includes('remaining') ? segs[0] : `${segs[0]} remaining`
    }
    if (phrase) return withOptionAfterRemaining(phrase, optionPeriod)
  }

  const tracksDone = tracksRemaining == null || tracksRemaining <= 0
  const daysDone = daysCd == null || daysCd.days <= 0
  if (tracksDone && daysDone && tailMonths == null) {
    if (tracksRemaining != null || daysCd != null) {
      return commitmentCompletedPhrase(
        commitmentEndDate(row, daysCd, null),
        optionPeriod,
      )
    }
    return 'No commitment data'
  }

  // Tracks done, days done/over, but tail still pending months
  if (tracksDone && daysDone && tailMonths != null) {
    return withOptionAfterRemaining(
      `0 tracks (+${tailMonths} month tail) remaining`,
      optionPeriod,
    )
  }

  if (daysCd) {
    if (daysCd.days <= 0 && tracksDone) {
      return commitmentCompletedPhrase(
        commitmentEndDate(row, daysCd, null),
        optionPeriod,
      )
    }
    return withOptionAfterRemaining(
      `${daysCd.days} days (${formatEndMdY(daysCd.end)}) remaining`,
      optionPeriod,
    )
  }
  return 'No commitment data'
}

function pendingMatchMonths(row, matchRaw) {
  const fromDisplay = pendingMonths(matchRaw)
  if (fromDisplay != null) return fromDisplay
  return parseIntOrNull(row?.['Match Length (Months)'])
}

function pendingLicenseYears(row, licenseRaw) {
  const fromDisplay = pendingYearsInt(licenseRaw)
  if (fromDisplay != null) return fromDisplay
  return parseIntOrNull(row?.['License Length (Years)'])
}

function buildInactiveLicenseMatchPhrase(row, matchRaw, licenseRaw, licenseExtend) {
  const matchMonths = pendingMatchMonths(row, matchRaw)
  const licenseYears = pendingLicenseYears(row, licenseRaw)
  if (matchMonths == null && licenseYears == null) return '—'

  const parts = []
  if (matchMonths != null) {
    parts.push(
      matchMonths === 0 ? 'match only for term' : `${matchMonths} month match`,
    )
  }
  if (licenseYears != null) {
    parts.push(`${licenseYears} year license`)
  }
  let body
  if (parts.length === 2) body = `${parts[0]} and ${parts[1]}`
  else body = parts[0]

  let out = body
  if (licenseYears != null) {
    out += extendSuffix(licenseExtend)
  }
  return out
}

function activeMatchPhrase(matchCd) {
  if (!matchCd || matchCd.days <= 0) return null
  return `${matchCd.days} days (${formatEndMdY(matchCd.end)}) remaining in match`
}

function activeLicensePhrase(yearsActive, licenseCd) {
  if (yearsActive != null) {
    if (yearsActive.end) {
      return `${yearsActive.years} years (${formatEndMdY(yearsActive.end)}) remaining in license`
    }
    return `${yearsActive.years} years remaining in license`
  }
  if (licenseCd && licenseCd.days > 0) {
    return `${licenseCd.days} days (${formatEndMdY(licenseCd.end)}) remaining in license`
  }
  return null
}

function buildLicenseMatchStatus(row) {
  const matchRaw = str(row?.['Match Remaining'])
  const licenseRaw = str(row?.['License Remaining'])
  const matchCd = parseDistroDayCountdown(matchRaw)
  const licenseCd = parseDistroDayCountdown(licenseRaw)
  const yearsActive = parseActiveLicenseYears(licenseRaw)
  const matchPending = pendingMonths(matchRaw) != null
  const licensePending = isPendingYears(licenseRaw)
  const licenseExtend = isYnYes(row?.['License Extend'])

  const matchPhrase = activeMatchPhrase(matchCd)
  const licensePhrase = activeLicensePhrase(yearsActive, licenseCd)
  if (matchPhrase || licensePhrase) {
    let out =
      matchPhrase && licensePhrase
        ? `${matchPhrase} and ${licensePhrase}`
        : matchPhrase || licensePhrase
    if (licensePhrase) out += extendSuffix(licenseExtend)
    return out
  }

  // Inactive static terms (not started counting down yet)
  if (matchPending || licensePending) {
    return buildInactiveLicenseMatchPhrase(row, matchRaw, licenseRaw, licenseExtend)
  }

  // Lengths configured but display empty — still show pending phrasing from lengths
  const matchLen = parseIntOrNull(row?.['Match Length (Months)'])
  const licenseLen = parseIntOrNull(row?.['License Length (Years)'])
  if (
    (matchLen != null || licenseLen != null) &&
    !matchCd &&
    !licenseCd &&
    !yearsActive
  ) {
    return buildInactiveLicenseMatchPhrase(row, matchRaw, licenseRaw, licenseExtend)
  }

  // Exhausted match/license countdowns
  if ((matchCd && matchCd.days <= 0) || (licenseCd && licenseCd.days <= 0)) {
    const matchOver = !matchRaw || matchPending || (matchCd && matchCd.days <= 0)
    const licenseOver =
      !licenseRaw || licensePending || (licenseCd && licenseCd.days <= 0)
    if (matchOver && licenseOver && (matchCd || licenseCd)) {
      return 'License/match complete'
    }
  }

  if (!matchRaw && !licenseRaw) return '—'
  return '—'
}

/**
 * @param {Record<string, unknown>} row
 * @param {{ today?: Date }} [opts]
 * @returns {{ 'Commitment Status': string, 'License/Match Status': string }}
 */
export function buildDistroSimplifyStatuses(row, opts = {}) {
  const today = opts.today instanceof Date ? opts.today : new Date()
  return {
    'Commitment Status': buildCommitmentStatus(row, today),
    'License/Match Status': buildLicenseMatchStatus(row),
  }
}

export default buildDistroSimplifyStatuses
